import { apiClient } from '@/core/api/client';
import { Endpoints } from '@/core/api/endpoints';
import { logger } from '@/core/logger';
import { useAppStore } from '@/core/stores/appStore';

export interface ServiceRequestDto {
    Id: string;
    caseNumber: string;
    subject: string;
    description: string;
    status: string | null;
    substatus: string | null;
    unitName: string | null;
    salesOrder: string | null;
    created_on: string;
    last_updated_on: string;
    closed_on: string | null;
}

export interface ServiceRequestListResponse {
    serviceRequests: ServiceRequestDto[];
}

export interface ServiceRequestCategory {
    id: string;
    name: string;
    key?: string;
}
export type { ServiceRequestCategory as ServiceRequestCategoryType };

// Cached / fallback list of service request types (from list-values API)
export const SERVICE_REQUEST_TYPES_PRESET: readonly ServiceRequestCategory[] = [
    { id: 'a4aFW0000001HDJYA2', name: 'Need My Payment Receipt', key: 'a4aFW0000001HDJYA2-0' },
    { id: 'a4aFW0000001JobYAE', name: 'Replace My Cheque', key: 'a4aFW0000001JobYAE-0' },
    { id: 'a4aFW0000001NXFYA2', name: 'Know My Payment Plan', key: 'a4aFW0000001NXFYA2-0' },
    { id: 'a4aFW0000001NXFYA2', name: 'Download My Statement of Account', key: 'a4aFW0000001NXFYA2-1' },
    { id: 'a4aFW0000001Ph7YAE', name: 'Want to Sell My Property', key: 'a4aFW0000001Ph7YAE-0' },
    { id: 'a4aFW0000001Zq9YAE', name: 'Unable to Register', key: 'a4aFW0000001Zq9YAE-0' },
    { id: 'a4aFW0000001Zq9YAE', name: 'Unable to Login', key: 'a4aFW0000001Zq9YAE-1' },
    { id: 'a4aFW0000001Zq9YAE', name: 'Unable to Pay Online', key: 'a4aFW0000001Zq9YAE-2' },
    { id: 'a4aFW0000001Zq9YAE', name: 'Getting an Error', key: 'a4aFW0000001Zq9YAE-3' },
    { id: 'a4aFW0000001Zq9YAE', name: 'App is Slow', key: 'a4aFW0000001Zq9YAE-4' },
    { id: 'a4aFW0000001Zq9YAE', name: 'Inquire About My Title Deed', key: 'a4aFW0000001Zq9YAE-5' },
    { id: 'a4aFW0000001Zq9YAE', name: 'Property Not Listed', key: 'a4aFW0000001Zq9YAE-6' },
    { id: 'a4aFW0000001Zq9YAE', name: 'Other – General Case', key: 'a4aFW0000001Zq9YAE-7' },
    { id: 'a4aJ70000010wZyIAI', name: 'Defer My Payment', key: 'a4aJ70000010wZyIAI-0' },
] as const;

export interface CreateServiceRequestPayload {
    requestTypeId: string;
    parentId?: string | null;
    countryCode?: string | null;
    phoneNumber?: string | null;
    email?: string | null;
    description: string;
    subject: string;
    category?: string | null;
    subCategory?: string | null;
    unitId?: string | null;
}

export interface CreateServiceRequestResponse {
    srNumber: string | null;
    srid: string;
}

type ServiceRequestsApiResponse = {
    status: string;
    message: string;
    data: {
        status: string;
        message: string;
        data: ServiceRequestListResponse;
    };
};

type ServiceRequestCategoriesApiResponse = {
    status: string;
    message: string;
    data: {
        serviceRequestTypes: {
            Name: string;
            Id: string;
        }[];
    };
};

type CreateServiceRequestApiResponse = {
    status: string;
    message: string;
    data: {
        status: string;
        message: string;
        data: {
            srNumber: string | null;
            srid: string;
        };
    };
};

/** Prefer explicit arg, else auth.user.customer_id (string or numeric from API). */
function resolveServiceRequestCustomerId(explicit?: string | null): string {
    const fromParam = explicit?.trim() ?? '';
    if (fromParam) {
        return fromParam;
    }
    const raw = useAppStore.getState().auth.user?.customer_id;
    if (typeof raw === 'string') {
        return raw.trim();
    }
    if (typeof raw === 'number' && Number.isFinite(raw)) {
        return String(raw);
    }
    return '';
}

export async function fetchServiceRequests(
    customerId?: string | null,
): Promise<ServiceRequestDto[]> {
    try {
        const resolvedCustomerId = resolveServiceRequestCustomerId(customerId);
        const isAdhaJourney = useAppStore.getState().isAdhaJourney;

        const res = await apiClient.get<ServiceRequestsApiResponse>(
            Endpoints.support.serviceRequests,
            !isAdhaJourney && resolvedCustomerId
                ? { params: { customer_id: resolvedCustomerId } }
                : undefined,
        );
        const body: any = res.data;
        const list = body?.data?.data?.serviceRequests ?? body?.data?.serviceRequests ?? body?.serviceRequests ?? [];
        logger.info('ServiceRequestService', 'Fetched service requests', {
            count: list.length,
            customer_id: resolvedCustomerId || null,
            isAdhaJourney,
        });
        return list;
    } catch (error: any) {
        logger.error('ServiceRequestService', 'Failed to fetch service requests', { error: error?.message || error });
        throw error;
    }
}

export async function fetchServiceRequestCategories(): Promise<ServiceRequestCategory[]> {
    try {
        const res = await apiClient.get<ServiceRequestCategoriesApiResponse>(
            Endpoints.support.serviceRequestCategories,
            { params: { listValues: 'SERVICE_REQUEST_LIST' } }
        );
        let rawList: any[] = (res?.data as any)?.serviceRequestTypes ?? (res?.data as any)?.data?.serviceRequestTypes ?? [];
        const { auth, isAdhaJourney } = useAppStore.getState();
        if (auth?.isAuthenticated) {
            if (isAdhaJourney) {
                rawList = rawList.filter((item: any) => item?.isAvailableForADHACustomer === true);
            } else {
                rawList = rawList.filter((item: any) => item?.isAvailableForADHACustomer === false);
            }
        }
        const mapped = rawList
            .filter((item: { Id?: string; Name?: string }) => item?.Id && item?.Name)
            .map((item: { Id: string; Name: string }): ServiceRequestCategory => ({
                id: item.Id,
                name: item.Name,
            }))
            .sort((a: ServiceRequestCategory, b: ServiceRequestCategory) => a.name.localeCompare(b.name));
        logger.info('ServiceRequestService', 'Fetched service request categories', { count: mapped.length });
        return mapped;
    } catch (error: any) {
        logger.error('ServiceRequestService', 'Failed to fetch service request categories', { error: error?.message || error });
        throw error;
    }
}

export async function createServiceRequest(payload: CreateServiceRequestPayload): Promise<CreateServiceRequestResponse> {
    try {
        const isAuthenticated = useAppStore.getState().auth.isAuthenticated;
        const body: CreateServiceRequestPayload = isAuthenticated
            ? {
                requestTypeId: payload.requestTypeId,
                parentId: payload.parentId,
                description: payload.description,
                subject: payload.subject,
                category: payload.category,
                subCategory: payload.subCategory,
                unitId: payload.unitId,
            }
            : payload;
        const res = await apiClient.post<CreateServiceRequestApiResponse>(Endpoints.support.createServiceRequest, body);
        const result = res?.data?.data;
        return {
            srNumber: result?.srNumber ?? null,
            srid: result?.srid ?? '',
        };
    } catch (error: any) {
        logger.error('ServiceRequestService', 'Failed to create service request', { error: error?.message || error });
        throw error;
    }
}

export default {
    fetchServiceRequests,
    fetchServiceRequestCategories,
    createServiceRequest,
};

